<template>
  <div>
    <template v-if="routesWithShippingMethodsList.length">
      <div class="shipping-costs-sheet">
        <div
          class="shipping-costs-item-wrap"
          :class="{
            'shipping-costs-item-wrap--collapsed':
              !expandedRoutes.includes(routeIndex),
          }"
          v-for="(route, routeIndex) in routesWithShippingMethodsList"
          :key="`${route.originCountry}-${route.destinationCountry}`"
        >
          <h4 class="shipping-costs-country-title">
            {{ $t("From")
            }}<span class="shipping-costs__country-name-wrap">
              <img
                class="shipping-costs__country-logo"
                :src="`https://flagcdn.com/w20/${route.originCountryShortcode.toLowerCase()}.png`"
              />{{ route.originCountry }}
            </span>
            {{ $t("To")
            }}<span class="shipping-costs__country-name-wrap">
              <img
                class="shipping-costs__country-logo"
                :src="`https://flagcdn.com/w20/${route.destinationCountryShortcode.toLowerCase()}.png`"
              />{{ route.destinationCountry }}
            </span>
            <span class="shipping-costs__number-of-routes"
              >{{ route.numberOfMethods }} shipping methods</span
            >
          </h4>
          <div
            class="shipping-costs-row"
            v-for="method in route.shippingMethods"
            :key="`${method.internal_code}-${Math.random()}`"
          >
            <div class="shipping-costs-text-column">
              <img
                :src="getCarrierLogo(method.carrier_code)"
                class="shipping-costs__carrier-logo"
              />
              <h4 class="shipping-costs__method-name">
                {{ method.friendly_name }}
              </h4>
              <div class="text-muted">
                {{ $t("Max.") }} {{ formatSize(method.max_dimension_l) }} x
                {{ formatSize(method.max_dimension_w) }} x
                {{ formatSize(method.max_dimension_h) }} cm,
                {{ formatWeight(method.min_weight, false) }} {{ $t("Up to") }}
                {{ formatWeight(method.max_weight) }}
              </div>
            </div>
            <div class="shipping-costs-price-column">
              <div class="shipping-costs-price">
                {{ formatNumber(method.small_shop) }}
              </div>
              <small class="text-center text-muted">Small Shop Plan</small>
            </div>
            <div class="shipping-costs-price-column">
              <div class="shipping-costs-price">
                {{ formatNumber(method.enterprise) }}
                <div
                  class="shipping-costs__price-label"
                  v-if="
                    cheapestPriceDifference(
                      method.small_shop,
                      method.enterprise
                    ) !== '0%'
                  "
                >
                  {{
                    cheapestPriceDifference(
                      method.small_shop,
                      method.enterprise
                    )
                  }}
                </div>
              </div>
              <small class="text-center text-muted">Business Plan</small>
            </div>
          </div>
          <div
            class="shipping-costs__show-more-background"
            v-if="!expandedRoutes.includes(routeIndex)"
          >
            <button
              class="shipping-costs__show-more-button"
              @click="expandedRoutes.push(routeIndex)"
            >
              <font-awesome-icon icon="arrow-down" />
              {{ $t("Show all") }}
            </button>
          </div>
        </div>
        <div class="shipping-costs-item-wrap">
          <h4 class="shipping-costs-country-title">
            {{ $t("Productivity savings") }}
          </h4>
          <div class="shipping-costs-row">
            <div
              class="shipping-costs-text-column shipping-costs-text-column--productivity"
            >
              <div class="text-muted">
                {{
                  $t(
                    "With Sendcloud, you'll save time on productivity and implementation costs. Here's an estimation of what you might save based on your volume."
                  )
                }}
                <a target="_blank" :href="getLearnMoreWebsiteLink()">{{
                  $t("Read more")
                }}</a>
              </div>
            </div>
            <div class="shipping-costs-price-column">
              <div class="shipping-costs-price">
                {{ volume }} {{ $t("parcels") }}
              </div>
              <small class="text-center text-muted">{{
                $t("Monthly volume")
              }}</small>
            </div>
            <div class="shipping-costs-price-column">
              <div class="shipping-costs-price">
                {{ productivitySavings }} {{ $t("hrs") }}
              </div>
              <small class="text-center text-muted">{{
                $t("Monthly savings")
              }}</small>
            </div>
          </div>
        </div>
        <div class="shipping-costs-results__footer">
          <div class="shipping-costs__text">
            {{ $t("Sign up to view all the shipping rates.") }}
          </div>
          <a
            href="https://panel.sendcloud.sc/accounts/signup/"
            target="_blank"
            class="shipping-costs-results__button"
          >
            {{ $t("Start shipping for free") }}
          </a>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="shipping-costs-sheet">
        <div class="shipping-costs__text shipping-costs__text--empty-state">
          {{
            $t(
              "We couldn't find any results. Please go back and try another query."
            )
          }}
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { getCurrentLanguage } from "@/lang/i18n.js";

export default {
  props: {
    routeItems: {
      type: Array,
      required: true,
    },
    shippingMethods: {
      type: Array,
      required: true,
    },
    volume: Number,
    weight: Number,
  },
  data() {
    return {
      expandedRoutes: [],
    };
  },
  computed: {
    productivitySavingsMinutes() {
      switch (true) {
        case this.volume <= 80:
          return 5;
        case this.volume <= 300:
          return 2.5;
        case this.volume <= 1000:
          return 1;
        default:
          return 0.3;
      }
    },
    productivitySavings() {
      return new Intl.NumberFormat("nl-NL", {
        maximumFractionDigits: 1,
      }).format((this.volume * this.productivitySavingsMinutes) / 60);
    },
    routesWithShippingMethodsList() {
      return this.routeItems
        .map((routeItem, index) => {
          //Filter methods on from + to country, filter out 0 pricing and filter out methods with higher min_weights.
          const methods = this.shippingMethods
            .filter(
              (method) =>
                method.from_country === routeItem.originCountryShortcode &&
                method.to_country === routeItem.destinationCountryShortcode &&
                method.essential > 0 &&
                Math.round(method.min_weight) <= Math.round(this.weight) &&
                Math.round(method.max_weight) >= Math.round(this.weight)
            )
            .sort((a, b) => {
              return a.small_shop - b.small_shop;
            });

          return {
            ...routeItem,
            numberOfMethods: methods.length,
            shippingMethods: this.expandedRoutes.includes(index)
              ? methods
              : methods.slice(0, 5), //To do, append all methods for 'view more' fields.
          };
        })
        .filter((item) => item.shippingMethods.length);
    },
  },
  methods: {
    getLearnMoreWebsiteLink() {
      switch (getCurrentLanguage()) {
        case "fr": {
          return "https://www.sendcloud.fr/fonctionnalites/expedition/";
        }
        case "nl": {
          return "https://www.sendcloud.nl/orders-verwerken/";
        }
        case "de": {
          return "https://www.sendcloud.de/shipping/";
        }
        case "it": {
          return "https://www.sendcloud.it/processare-gli-ordini-2/";
        }
        case "es": {
          return "https://www.sendcloud.es/envios/";
        }
        default: {
          return "https://www.sendcloud.com/process-orders/";
        }
      }
    },
    getCarrierLogo(carrier) {
      try {
        return require(`@/assets/carrier_logos/${carrier}.svg`);
      } catch (e) {} //eslint-disable-line
    },
    cheapestPriceDifference(methodEnterprise, methodSmallShop) {
      return this.formatPercentage(
        100 - (methodEnterprise / methodSmallShop) * 100
      );
    },
    formatPercentage(number) {
      return new Intl.NumberFormat("nl-NL", {
        style: "unit",
        unit: "percent",
        maximumFractionDigits: 1,
      }).format(number);
    },
    formatNumber(number) {
      return new Intl.NumberFormat("nl-NL", {
        style: "currency",
        currency: "EUR",
      }).format(number);
    },
    formatSize(number) {
      if (number < 1) return "–";
      return parseInt(number).toFixed(0);
    },
    formatWeight(number, includeUnit = true) {
      const options = {
        maximumSignificantDigits: 2,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      };
      if (includeUnit) {
        options.unit = "kilogram";
        options.style = "unit";
      }
      if (number < 1 && !includeUnit) return null;

      return new Intl.NumberFormat("nl-NL", options).format(number);
    },
  },
};
</script>

<style scoped>
.shipping-costs__method-name {
  display: inline-block;
  margin: 0px 0px 5px 0px;
  font-size: 1em;
  font-weight: 700;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.shipping-costs__carrier-logo[src] {
  object-fit: cover;
  display: inline-block;
  max-width: 30px;
  max-height: 20px;
  margin: -20px 5px 0px 0px;
}
.shipping-costs-country-title {
  margin: 20px 0px 0px;
  font-size: 1.2em;
  font-weight: 700;
  line-height: 2;
}
.shipping-costs-price {
  display: flex;
  align-items: center;
  margin: 0px 0px 0px;
  font-weight: 600;
  text-align: center;
}
.shipping-costs-price-column {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 20%;
  flex-shrink: 0;
  padding: 5px;
}
.shipping-costs-row {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  margin: 10px 0px 10px 20px;
  padding: 10px 0px;
  border-bottom: 1px solid rgb(221, 225, 230);
}
.shipping-costs-text-column {
  position: relative;
  flex-grow: 1;
  padding: 5px;
  overflow: hidden;
  white-space: nowrap;
}

.shipping-costs-text-column--productivity {
  max-width: 60%;
  white-space: initial;
}

.shipping-costs__text {
  font-size: 16px;
  color: rgb(81, 90, 107);
}

.shipping-costs__text--empty-state {
  padding: 30px;
  text-align: center;
}

.shipping-costs__price-label {
  display: inline-block;
  padding: 2px 6px;
  margin-left: 4px;
  background: rgb(68, 253, 173);
  border-radius: 24px;
  font-weight: 700;
  font-size: 0.8em;
  color: rgb(2, 88, 50);
  text-align: center;
}
.text-muted {
  color: rgb(100, 111, 132);
}
.shipping-costs-sheet {
  display: flex;
  flex-direction: column;
  padding: 15px 30px 45px 30px;
  background: rgb(240, 244, 246);
  border-radius: 18px;
  font-family: "Roboto", sans-serif;
  color: #123958;
  max-width: 750px;
  margin: 0px auto;
}

.shipping-costs-results__footer {
  margin: 15px -30px 0px;
  padding: 0px 30px 0px 30px;
  background-size: contain;
  flex-shrink: 0;
}

.shipping-costs-results__button {
  display: inline-block;
  margin: 30px 0px 15px;
  padding: 8px 16px;
  background: rgb(29, 151, 255);
  border: none;
  border-radius: 48px;
  transition: background 0.25s ease-in-out 0s;
  font-weight: 500;
  color: #ffffff;
}

.shipping-costs-results__button:hover {
  text-decoration: none;
}

.shipping-costs-results__button:focus {
  box-shadow: 0px 0px 0px 2px rgba(0, 0, 0, 0.1);
}
.shipping-costs-item-wrap {
  flex: 1 0;
  margin-bottom: 30px;
  overflow: hidden;
  position: relative;
}
.shipping-costs-item-wrap--collapsed {
  max-height: 350px;
}

.shipping-costs__show-more-background {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  position: absolute;
  width: 100%;
  height: 150px;
  bottom: 0;
  background: linear-gradient(rgba(0, 0, 0, 0), rgb(240, 244, 246));
}

.shipping-costs__show-more-button {
  position: absolute;
  bottom: 10px;
  align-self: center;
  background: rgb(240, 244, 246);
  border: 1px solid rgb(231, 231, 231);
  padding: 8px 32px;
  border-radius: 45px;
  box-shadow: 0px 0px 10px 0px rgb(207, 213, 216, 0.5);
  transition: box-shadow 0.2s ease-in-out, background 0.2s ease-in-out;
  font-weight: 600;
  font-size: 0.9rem;
  line-height: 1em;
  color: #122857;
}

.shipping-costs__country-name-wrap {
  padding: 4px 12px;
  margin: 0px 8px;
  border-radius: 25px;
  border: 1px solid rgb(208, 212, 218);
  font-size: 0.9em;
  white-space: nowrap;
}

.shipping-costs__country-logo {
  width: 18px;
  display: inline-block;
  margin-right: 5px;
  margin-top: -1px;
}

.shipping-costs__number-of-routes {
  font-size: 0.8rem;
  font-weight: 300;
  color: rgb(100, 111, 132);
}

@media screen and (max-width: 768px) {
  .shipping-costs-sheet {
    padding: 15px;
  }

  .shipping-costs-country-title {
    font-size: 1rem;
  }

  .shipping-costs-row {
    margin: 0;
  }

  .shipping-costs-text-column {
    width: 100%;
    flex-shrink: 0;
  }

  .shipping-costs-price-column {
    align-items: flex-start;
    padding-top: 10px;
    width: 50%;
  }

  .shipping-costs-text-column--productivity {
    max-width: 100%;
  }
}
</style>
